import { useState, useCallback, useRef } from 'react';
import {
  PenTool,
  PenLine,
  Calendar,
  Type,
  CheckSquare,
  X,
} from 'lucide-react';

const KIND_ICONS = {
  signature: PenTool,
  initials: PenLine,
  date: Calendar,
  text: Type,
  checkbox: CheckSquare,
};

// Default field sizes in *PDF points*. Converted to percentages at placement
// time so the stored values are resolution- and scale-independent.
const DEFAULT_SIZES_PT = {
  signature: { width: 200, height: 60 },
  initials: { width: 100, height: 40 },
  date: { width: 150, height: 25 },
  text: { width: 200, height: 25 },
  checkbox: { width: 20, height: 20 },
};

const MIN_WIDTH_PCT = 2;
const MIN_HEIGHT_PCT = 1.5;

export default function FieldPlacer({
  pageNum,
  pageWidth,
  pageHeight,
  zoom,
  fields,
  activeRecipientId,
  activeFieldKind,
  onAddField,
  onRemoveField,
  onUpdateField,
  recipientColors,
}) {
  const [dragState, setDragState] = useState(null); // { fieldId, startX, startY, origX, origY } (percentages)
  const [resizeState, setResizeState] = useState(null); // { fieldId, startX, startY, origW, origH } (percentages)
  const overlayRef = useRef(null);

  // Convert a mouse event to percentage coordinates (0-100) relative to the page.
  const toPct = useCallback((e) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  // Click to place a new field
  const handleOverlayClick = useCallback(
    (e) => {
      if (!activeFieldKind || !activeRecipientId) return;
      if (e.target !== overlayRef.current) return;

      const { x, y } = toPct(e);
      const sizePt = DEFAULT_SIZES_PT[activeFieldKind];
      const widthPct = (sizePt.width / pageWidth) * 100;
      const heightPct = (sizePt.height / pageHeight) * 100;

      // Center the field on the click point and clamp to the page.
      const fx = Math.max(0, Math.min(x - widthPct / 2, 100 - widthPct));
      const fy = Math.max(0, Math.min(y - heightPct / 2, 100 - heightPct));

      onAddField({
        recipient_id: activeRecipientId,
        kind: activeFieldKind,
        required: true,
        page_num: pageNum,
        x: fx,
        y: fy,
        width: widthPct,
        height: heightPct,
        label: activeFieldKind.charAt(0).toUpperCase() + activeFieldKind.slice(1),
      });
    },
    [activeFieldKind, activeRecipientId, toPct, pageWidth, pageHeight, pageNum, onAddField],
  );

  // Drag to move
  const handleFieldMouseDown = useCallback(
    (e, field) => {
      e.stopPropagation();
      e.preventDefault();
      setDragState({
        fieldId: field.id,
        startX: e.clientX,
        startY: e.clientY,
        origX: field.x,
        origY: field.y,
      });
    },
    [],
  );

  // Resize handle
  const handleResizeMouseDown = useCallback(
    (e, field) => {
      e.stopPropagation();
      e.preventDefault();
      setResizeState({
        fieldId: field.id,
        startX: e.clientX,
        startY: e.clientY,
        origW: field.width,
        origH: field.height,
      });
    },
    [],
  );

  const handleMouseMove = useCallback(
    (e) => {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      if (dragState) {
        const dxPct = ((e.clientX - dragState.startX) / rect.width) * 100;
        const dyPct = ((e.clientY - dragState.startY) / rect.height) * 100;
        const field = fields.find((f) => f.id === dragState.fieldId);
        if (!field) return;
        const newX = Math.max(0, Math.min(dragState.origX + dxPct, 100 - field.width));
        const newY = Math.max(0, Math.min(dragState.origY + dyPct, 100 - field.height));
        onUpdateField(dragState.fieldId, { x: newX, y: newY });
      } else if (resizeState) {
        const dxPct = ((e.clientX - resizeState.startX) / rect.width) * 100;
        const dyPct = ((e.clientY - resizeState.startY) / rect.height) * 100;
        const newW = Math.max(MIN_WIDTH_PCT, Math.min(resizeState.origW + dxPct, 100));
        const newH = Math.max(MIN_HEIGHT_PCT, Math.min(resizeState.origH + dyPct, 100));
        onUpdateField(resizeState.fieldId, { width: newW, height: newH });
      }
    },
    [dragState, resizeState, fields, onUpdateField],
  );

  const handleMouseUp = useCallback(() => {
    setDragState(null);
    setResizeState(null);
  }, []);

  const isPlacing = activeFieldKind && activeRecipientId;

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0"
      style={{
        cursor: isPlacing ? 'crosshair' : 'default',
        width: pageWidth * zoom,
        height: pageHeight * zoom,
      }}
      onClick={handleOverlayClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {fields.map((field) => {
        const color = recipientColors[field.recipient_id] || '#6366F1';
        const Icon = KIND_ICONS[field.kind] || Type;
        const isDragging = dragState?.fieldId === field.id;
        const isResizing = resizeState?.fieldId === field.id;
        // Approximate pixel height of the field for icon sizing (zoom is already baked
        // into the overlay's clientHeight via its wrapper styles).
        const pxHeight = (field.height / 100) * pageHeight * zoom;

        return (
          <div
            key={field.id}
            className="absolute group"
            style={{
              left: `${field.x}%`,
              top: `${field.y}%`,
              width: `${field.width}%`,
              height: `${field.height}%`,
              borderWidth: 2,
              borderStyle: field.required ? 'solid' : 'dashed',
              borderColor: color,
              backgroundColor: color + '1A', // 10% opacity
              borderRadius: 4,
              cursor: isDragging ? 'grabbing' : 'grab',
              zIndex: isDragging || isResizing ? 20 : 10,
              userSelect: 'none',
            }}
            onMouseDown={(e) => handleFieldMouseDown(e, field)}
          >
            {/* Kind icon */}
            <div
              className="absolute top-0.5 left-0.5 flex items-center gap-0.5"
              style={{ pointerEvents: 'none' }}
            >
              <Icon
                className="shrink-0"
                style={{
                  width: Math.min(14, pxHeight * 0.5),
                  height: Math.min(14, pxHeight * 0.5),
                  color,
                }}
              />
              {pxHeight > 20 && (
                <span
                  className="text-xs truncate leading-none"
                  style={{ color, fontSize: Math.min(11, pxHeight * 0.4) }}
                >
                  {field.label}
                </span>
              )}
            </div>

            {/* Remove button */}
            <button
              type="button"
              className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ zIndex: 25 }}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveField(field.id);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <X className="w-2.5 h-2.5" />
            </button>

            {/* Resize handle */}
            <div
              className="absolute bottom-0 right-0 w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                cursor: 'nwse-resize',
                backgroundColor: color,
                borderRadius: '1px 0 4px 0',
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, field)}
            />
          </div>
        );
      })}
    </div>
  );
}
